'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { MarkdownPreview } from '../../../../markdown-preview';
import {
  CheckmarkCircle01Icon,
  Clock01Icon,
  CancelCircleIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { ReadOnlyCodeEditor } from '../../ReadOnlyCodeEditor';

type PythonRendererProps = {
  data: Record<string, unknown> & {
    script?: string;
    credentialId?: string;
    output?: unknown;
    result?: unknown;
  };
  maxHeight?: string;
};
type ParsedPythonOutput = {
  success?: boolean;
  result?: unknown;
  jsonResult?: unknown;
  stdout?: string[];
  stderr?: string[];
  codeRuntime?: number;
};

function tryFormatAsJson(value: string): string | null {
  try {
    const parsed = JSON.parse(value);

    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
}

function parsePythonOutput(output: unknown): ParsedPythonOutput | null {
  if (typeof output === 'object' && output !== null) {
    const parsedObj = output as Record<string, unknown>;
    const hasStructuredFields =
      typeof parsedObj.success === 'boolean' ||
      Array.isArray(parsedObj.stdout) ||
      Array.isArray(parsedObj.stderr) ||
      parsedObj.result !== undefined ||
      parsedObj.jsonResult !== undefined;

    if (hasStructuredFields) {
      return parsedObj as ParsedPythonOutput;
    }

    return null;
  }

  if (typeof output !== 'string') {
    return null;
  }

  try {
    // Remove [python-tool] prefix if present
    let cleanedOutput = output;
    const prefixMatch = output.match(/^\[python-tool\]\s*/);

    if (prefixMatch) {
      cleanedOutput = output.slice(prefixMatch[0].length);
    }

    const parsed = JSON.parse(cleanedOutput);

    if (typeof parsed === 'object' && parsed !== null) {
      const parsedObj = parsed as Record<string, unknown>;
      const hasStructuredFields =
        typeof parsedObj.success === 'boolean' ||
        Array.isArray(parsedObj.stdout) ||
        Array.isArray(parsedObj.stderr);

      if (hasStructuredFields) {
        return parsed as ParsedPythonOutput;
      }
    }
  } catch {
    // Not JSON, return null
  }

  return null;
}

export function PythonRenderer({
  data,
  maxHeight = '300px',
}: PythonRendererProps) {
  const tCoder = useTranslations('designSystem.chat.coder');
  const tChat = useTranslations('designSystem.chat');
  const { script, credentialId, output, result } = data;
  const outputValue = output ?? result;
  const parsedOutput =
    outputValue !== undefined ? parsePythonOutput(outputValue) : null;
  const fallbackOutput =
    typeof outputValue === 'string'
      ? outputValue
      : outputValue !== undefined
        ? JSON.stringify(outputValue, null, 2)
        : null;

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {/* Credential badge */}
      {credentialId && (
        <Badge size="sm" variant="outline">
          {'Authenticated'}
        </Badge>
      )}

      {/* Script */}
      {script && (
        <ReadOnlyCodeEditor
          value={script}
          maxHeight={maxHeight}
          language="python"
        />
      )}

      {/* Output */}
      {outputValue !== undefined && (
        <VStack alignItems="start" gap={1.5} width="100%">
          {parsedOutput ? (
            <>
              {/* Execution Status */}
              <HStack gap={2} alignItems="center" flexWrap="wrap">
                {parsedOutput.success !== undefined && (
                  <Badge
                    size="sm"
                    variant="emphasis"
                    icon={
                      parsedOutput.success ? (
                        <CheckmarkCircle01Icon size={10} />
                      ) : (
                        <CancelCircleIcon size={10} />
                      )
                    }
                  >
                    {parsedOutput.success ? tCoder('success') : tChat('failed')}
                  </Badge>
                )}
                {parsedOutput.codeRuntime !== undefined && (
                  <Badge
                    size="sm"
                    variant="outline"
                    icon={<Clock01Icon size={10} />}
                    fontWeight="medium"
                  >
                    {`${parsedOutput.codeRuntime}ms`}
                  </Badge>
                )}
              </HStack>

              {/* Standard Output */}
              {parsedOutput.stdout && parsedOutput.stdout.length > 0 && (
                <VStack alignItems="start" gap={1} width="100%">
                  <Text fontSize="xs" fontWeight="medium" color="fg.default">
                    {'Output'}
                  </Text>
                  <Box
                    bg="bg.canvas"
                    p={2}
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    width="100%"
                    maxHeight={maxHeight}
                    overflow="auto"
                  >
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      color="fg.default"
                      whiteSpace="pre-wrap"
                    >
                      {parsedOutput.stdout.join('\n')}
                    </Text>
                  </Box>
                </VStack>
              )}

              {/* Standard Error */}
              {parsedOutput.stderr && parsedOutput.stderr.length > 0 && (
                <VStack alignItems="start" gap={1} width="100%">
                  <Text fontSize="xs" fontWeight="medium" color="fg.error">
                    {'Error Output'}
                  </Text>
                  <Box
                    bg="bg.canvas"
                    p={2}
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="border.error"
                    width="100%"
                    maxHeight={maxHeight}
                    overflow="auto"
                  >
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      color="fg.error"
                      whiteSpace="pre-wrap"
                    >
                      {parsedOutput.stderr.join('\n')}
                    </Text>
                  </Box>
                </VStack>
              )}

              {/* Result (try to format as JSON) - only show if different from stdout */}
              {parsedOutput.result != null &&
                parsedOutput.result !== 'null' &&
                // Don't show Result if it's the same as stdout content
                parsedOutput.stdout?.join('\n') !== parsedOutput.result && (
                  <VStack alignItems="start" gap={1} width="100%">
                    <Text fontSize="xs" fontWeight="medium" color="fg.default">
                      {'Result'}
                    </Text>
                    <Box
                      bg="bg.canvas"
                      p={2}
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor="border.subtle"
                      width="100%"
                      maxHeight={maxHeight}
                      overflow="auto"
                    >
                      {typeof parsedOutput.result === 'string' &&
                      tryFormatAsJson(parsedOutput.result) ? (
                        <MarkdownPreview
                          source={`\`\`\`json\n${tryFormatAsJson(parsedOutput.result)}\n\`\`\``}
                        />
                      ) : (
                        <Text
                          fontSize="xs"
                          fontFamily="mono"
                          color="fg.subtle"
                          whiteSpace="pre-wrap"
                        >
                          {typeof parsedOutput.result === 'string'
                            ? parsedOutput.result
                            : JSON.stringify(parsedOutput.result, null, 2)}
                        </Text>
                      )}
                    </Box>
                  </VStack>
                )}

              {/* JSON Result (legacy, if not null) */}
              {parsedOutput.jsonResult != null &&
                parsedOutput.jsonResult !== 'null' &&
                !parsedOutput.result && (
                  <VStack alignItems="start" gap={1} width="100%">
                    <Text fontSize="xs" fontWeight="medium" color="fg.default">
                      {'JSON Result'}
                    </Text>
                    <Box
                      bg="bg.canvas"
                      p={2}
                      borderRadius="sm"
                      borderWidth="1px"
                      borderColor="border.subtle"
                      width="100%"
                      maxHeight={maxHeight}
                      overflow="auto"
                    >
                      <Text
                        fontSize="xs"
                        fontFamily="mono"
                        color="fg.subtle"
                        whiteSpace="pre-wrap"
                      >
                        {typeof parsedOutput.jsonResult === 'string'
                          ? parsedOutput.jsonResult
                          : JSON.stringify(parsedOutput.jsonResult, null, 2)}
                      </Text>
                    </Box>
                  </VStack>
                )}
            </>
          ) : (
            // Fallback for non-structured output
            <VStack alignItems="start" gap={1} width="100%">
              <Text fontSize="xs" fontWeight="medium" color="fg.default">
                {'Output'}
              </Text>
              <Box
                bg="bg.canvas"
                p={2}
                borderRadius="sm"
                borderWidth="1px"
                borderColor="border.subtle"
                width="100%"
                maxHeight={maxHeight}
                overflow="auto"
              >
                <Text
                  fontSize="xs"
                  fontFamily="mono"
                  color="fg.subtle"
                  whiteSpace="pre-wrap"
                >
                  {fallbackOutput}
                </Text>
              </Box>
            </VStack>
          )}
        </VStack>
      )}
    </VStack>
  );
}
